/* eslint-disable @typescript-eslint/naming-convention */
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, ViewChild } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { CarModelTypeService } from 'src/app/car-model-type/car-model-type.service';
import { CartService } from 'src/app/cart/cart.service';
import { LabelPrintComponent } from 'src/app/computer/label-print/label-print.component';
import { FolderService } from 'src/app/folder/folder.service';
import { AddImageComponent } from 'src/app/product/add-image/add-image.component';
import { AnalogueListComponent } from 'src/app/product/analogue-list/analogue-list.component';
import { CardComponent } from 'src/app/product/card/card.component';
import { MoveComponent } from 'src/app/product/move/move.component';
import { NameUpdateComponent } from 'src/app/product/name-update/name-update.component';
import { ProductService } from 'src/app/product/product.service';
import { RemoveDoubleComponent } from 'src/app/product/remove-double/remove-double.component';
import { SelectorComponent } from 'src/app/product/selector/selector.component';
import { SetNewPriceComponent } from 'src/app/product/set-new-price/set-new-price.component';
import { setLoading } from 'src/app/redux/actions/user.actions';
import { AppState } from 'src/app/redux/app.state';
import { SupportService } from 'src/app/support.service';
import { CarModelType, Folder, InfoRow, Product, ProductInCart, User } from 'src/typings';
import { environment } from 'src/environments/environment';
import { AddInfoRowComponent } from '../add-info-row/add-info-row.component';

interface ExampleFlatNode {
  expandable: boolean;
  name: string;
  id: number;
  level: number;
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class ProfileComponent {
  @ViewChild('paginator') paginator: MatPaginator | undefined;

  car: CarModelType | undefined;

  products: UntypedFormArray = this.fb.array([]);

  user: User | undefined;

  tableProducts: Product[] = [];

  folders: Folder[] = [];

  set loading(value: boolean) {
    this.store.dispatch(setLoading({ payload: value }));
  }

  cartProductsFrom: UntypedFormArray = this.fb.array([]);

  _cartProducts: ProductInCart[] = [];

  set cartProducts(products: ProductInCart[]) {
    this._cartProducts = products;
    this.cartProductsFrom.clear();
    products.forEach((prod) => {
      this.cartProductsFrom.push(this.fb.group(prod));
    });
  }

  selectedFolder: ExampleFlatNode | undefined;

  pageSize = 50;

  resultsLength = 0;

  sort_active = 'description';

  sort_direction = 'asc';

  columnsToDisplay = [
    'id',
    'img',
    'description',
    'brand',
    'article',
    'price',
    'stock',
    'cell',
    'menu'
  ];

  constructor(
    private carService: CarModelTypeService,
    private activateRoute: ActivatedRoute,
    private dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    private productService: ProductService,
    public supportService: SupportService,
    private folderService: FolderService,
    private cartService: CartService,
    private titleService: Title
  ) {
    this.carService.carModelTypeProductGw.subscribe((data) => {
      const { car_id } = data;
      if (!this.car) return;
      if (this.car.id === car_id) this.searchByPage();
    });
    this.productService.productGw.subscribe((list) => {
      if (!list) return;
      this.updateProducts(list);
    });
    this.cartService.cartSocket.subscribe((cart) => {
      if (!this.user || !this.user.cart) return;
      if (cart.id === this.user.cart.id && cart.products) {
        this.cartProducts = cart.products.map((prod) => {
          return { ...prod, quan: Number(prod.quan) };
        });
      }
    });
    this.activateRoute.params.subscribe((params) => {
      this.updateInfoCar(params.id);
    });
    this.store.subscribe((data) => {
      this.user = data.user.info;
      this.updateCartProducts();
    });
  }

  updateCartProducts() {
    if (!this.user || !this.user.cart) return;
    this.cartService.getOne(this.user.cart.id).subscribe((cart) => {
      if (cart.products)
        this.cartProducts = cart.products.map((prod) => {
          return { ...prod, quan: Number(prod.quan) };
        });
    });
  }

  clearFolder() {
    this.selectedFolder = undefined;
    this.getProducts();
  }

  removeDouble(product: Product) {
    const dialog = this.dialog.open(RemoveDoubleComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data === true) dialog.close();
    });
  }

  addImage(product: Product) {
    const dialog = this.dialog.open(AddImageComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data) dialog.close();
      this.updateProducts(data);
    });
  }

  updateProducts(list: Product[]) {
    if (!list) return;
    const updatedList = this.tableProducts;
    this.tableProducts = [];
    for (let index = 0; index < list.length; index++) {
      const product = list[index];
      const foundIndex = updatedList.findIndex((prod) => prod.id === product.id);
      updatedList[foundIndex] = {
        ...updatedList[foundIndex],
        ...product
      };
    }
    this.tableProducts = [...updatedList];
  }

  updateName(product: Product) {
    const dialog = this.dialog.open(NameUpdateComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data) dialog.close();
      this.updateProducts([data]);
    });
  }

  setNewPrice(product: Product) {
    const dialogRef = this.dialog.open(SetNewPriceComponent);
    dialogRef.componentInstance.product = product;
    dialogRef.componentInstance.saveEvent.subscribe((value) => {
      if (value) dialogRef.close();
      this.updateProducts([value]);
    });
  }

  updateInfoCar(id: number) {
    this.loading = true;
    this.carService.getOne(id).subscribe((car) => {
      this.car = car;
      this.updateArray();
      this.loading = false;
      this.getProducts();
      this.titleService.setTitle(
        `${this.car?.carModel?.manufacturer?.description} | ${this.car?.carModel?.description}`
      );
    });
    this.folderService.getNestedListFolders().subscribe((folders) => {
      this.folders = folders;
      this.updateFolders();
    });
  }

  getProducts(folder?: ExampleFlatNode) {
    if (!this.user) return;
    this.selectedFolder = folder;
    this.loading = true;
    this.productService
      .search({
        default_warehouse_id: this.user.defaultWarehouse.id,
        folders_id: folder ? [folder.id] : undefined,
        car_id: this.car?.id,
        limit: this.pageSize,
        offset: 0,
        sort_active: this.sort_active,
        sort_direction: this.sort_direction
      })
      .subscribe((products) => {
        this.tableProducts = products.data;
        this.loading = false;
        this.resultsLength = products.length;
      });
  }

  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger | undefined;

  menuTopLeftPosition = { x: '0', y: '0' };

  onRightClick(event: MouseEvent, item: Product) {
    if (!this.matMenuTrigger) return;
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    // we open the menu
    // we pass to the menu the information about our object
    this.matMenuTrigger.menuData = { item: item };

    // we open the menu
    this.matMenuTrigger.openMenu();
  }

  private _transformer = (node: Folder, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.description,
      id: node.id,
      level: level
    };
  };

  treeControl = new FlatTreeControl<ExampleFlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: ExampleFlatNode) => node.expandable;

  updateFolders() {
    this.dataSource.data = [];
    this.dataSource.data = this.folders;
  }

  updateArray() {
    if (this.car && this.car.products) {
      this.products = new UntypedFormArray(
        this.car.products.map((prod) => {
          return this.fb.group(prod);
        })
      );
    }
  }

  getEngineDescription() {
    return this.car?.engines.map((engine) => engine.description).join(', ');
  }

  getHorsePower() {
    return `${this.car?.engines[0].horsePower} л.с. ${this.car?.engines[0].kW} кВт`;
  }

  removeFromCart(product: Product) {
    if (!this.user || !this.user.cart) return;
    // this.loading = true;
    this.cartService
      .removeProduct({
        id: this.user.cart.id,
        product_id: product.id
      })
      .subscribe(
        () => {
          // this.loading = false;
        },
        () => {
          // this.loading = false;
        }
      );
  }

  productInCart(id: number) {
    const index = this._cartProducts.findIndex((prod) => prod.product.id === id);
    return index > -1;
  }

  getProductInCartGroup(product_id: number) {
    return this.cartProductsFrom.controls.find((ctrl) => {
      return ctrl.value.product.id === product_id;
    }) as UntypedFormGroup;
  }

  getProductCartControl(product_id: number) {
    const prodCtrl = this.cartProductsFrom.controls.find((ctrl) => {
      return ctrl.value.product.id === product_id;
    }) as UntypedFormGroup;
    if (!prodCtrl) return new UntypedFormControl();
    return prodCtrl.controls.quan as UntypedFormControl;
  }

  changeCartQuan(product_id: number) {
    const group = this.getProductInCartGroup(product_id);
    const control = this.getProductCartControl(product_id);
    this.cartService
      .updateProductInCart({
        id: group.value.id,
        quan: control.value,
        price: group.value.price
      })
      .subscribe(() => {
        return;
      });
  }

  addToCart(product: Product) {
    if (!this.user || !this.user.cart) return;
    // this.loading = true;
    this.cartService
      .addProduct({
        id: this.user.cart.id,
        product_id: product.id
      })
      .subscribe(
        () => {
          // this.loading = false;
        },
        () => {
          // this.loading = false;
        }
      );
  }

  openPrintDialog(product: Product) {
    const printDialog = this.dialog.open(LabelPrintComponent);
    printDialog.componentInstance.productId = product.id;
    if (product.barcodes && product.barcodes.length > 0) {
      printDialog.componentInstance.barcode = product.barcodes[0].barcode;
    }
  }

  openMoveProd(id: number) {
    const dialogRef = this.dialog.open(MoveComponent, {
      width: '1500px',
      height: '800px'
    });
    dialogRef.componentInstance.product_id = id;
  }

  getWarehouse(product: Product) {
    if (!this.user) return;
    const warehouse = product.stock.find((wh) => {
      return wh.warehouse.id === this.user?.defaultWarehouse.id;
    });
    return warehouse;
  }

  sortStock(product: Product) {
    if (!this.user) return;
    const stock = product.stock.sort((a, b) => {
      if (a.warehouse.id === this.user?.defaultWarehouse.id) return -1;
      if (b.warehouse.id === this.user?.defaultWarehouse.id) return 1;
      return 0;
    });
    return stock;
  }

  addInfo() {
    const dialogRef = this.dialog.open(AddInfoRowComponent);
    dialogRef.componentInstance.car_id = this.car?.id;
    dialogRef.componentInstance.changeEvent.subscribe((data) => {
      if (this.car) {
        this.car.info_row = data;
        dialogRef.close();
      }
    });
  }

  editInfo(row: InfoRow) {
    const dialogRef = this.dialog.open(AddInfoRowComponent);
    dialogRef.componentInstance.row_id = row.id;
    dialogRef.componentInstance.description = row.description;
    dialogRef.componentInstance.value = row.value;
    dialogRef.componentInstance.changeEvent.subscribe((data) => {
      if (this.car) {
        this.car.info_row = data;
        dialogRef.close();
      }
    });
  }

  deleteRow(id: number) {
    this.carService.removeInfoRow(id).subscribe((data) => {
      if (this.car) {
        this.car.info_row = data;
      }
    });
  }

  addItem() {
    const dialogRef = this.dialog.open(SelectorComponent, {
      width: '1200px'
    });
    dialogRef.componentInstance.controls = this.products;
    dialogRef.componentInstance.defaultWarehouse = this.user?.defaultWarehouse;
    dialogRef.componentInstance.car = this.car;

    dialogRef.componentInstance.addProductEvent.subscribe((product) => {
      this.addProductToCar(product);
    });
    dialogRef.componentInstance.removeProductEvent.subscribe((product) => {
      this.removeProductFromCar(product);
    });
  }

  removeProduct(product: Product) {
    if (!this.car) return;
    this.loading = true;
    this.carService
      .removeProductFromCar({
        car_id: this.car.id,
        product_id: product.id
      })
      .subscribe(() => {
        if (!this.car) return;
        this.loading = false;
        this.updateInfoCar(this.car.id);
        if (!this.selectedFolder) return;
        this.getProducts(this.selectedFolder);
      });
  }

  addProductToCar(product: Product) {
    if (this.car) {
      this.carService
        .addProductToCar({
          car_id: this.car.id,
          product_id: product.id
        })
        .subscribe(() => {
          this.car?.products?.push(product);
          this.updateArray();
          if (this.car) this.updateInfoCar(this.car.id);
        });
    }
  }

  removeProductFromCar(product: Product) {
    if (this.car) {
      this.carService
        .removeProductFromCar({
          car_id: this.car.id,
          product_id: product.id
        })
        .subscribe(() => {
          const index = this.car?.products?.findIndex((prod) => prod.id === product.id);
          if (index !== -1 && index !== undefined) {
            this.car?.products?.splice(index, 1);
            this.updateArray();
            if (this.car) this.updateInfoCar(this.car.id);
          }
        });
    }
  }

  openDialog(product: Product) {
    const dialogRef = this.dialog.open(CardComponent, {
      width: '1700px',
      height: '800px'
    });
    dialogRef.componentInstance.product = product;
    if (this.user) dialogRef.componentInstance.defaultWarehouse = this.user.defaultWarehouse;
  }

  sortData(event: any) {
    this.sort_active = event.active;
    this.sort_direction = event.direction;
    if (this.paginator) this.paginator.pageIndex = 0;
    this.searchByPage();
  }

  searchByPage(page = 0) {
    if (!this.user) return;
    this.loading = true;
    this.productService
      .search({
        default_warehouse_id: this.user.defaultWarehouse.id,
        folders_id: this.selectedFolder ? [this.selectedFolder.id] : undefined,
        car_id: this.car?.id,
        limit: this.pageSize,
        offset: page * this.pageSize,
        sort_active: this.sort_active,
        sort_direction: this.sort_direction
      })
      .subscribe((data) => {
        this.loading = false;
        this.tableProducts = data.data;
        this.resultsLength = data.length;
      });
  }

  updateSelected() {
    // @todo improve
    console.log(this.car);
  }

  getImg(product: Product) {
    if (!product.images || product.images.length === 0)
      return `${environment.BACKEND}/images/nophoto.jpg`;
    const img = product.images[0].name;
    return `${environment.BACKEND}/image/${img}`;
  }

  openAnalogies(product: Product) {
    const dialog = this.dialog.open(AnalogueListComponent, {
      height: '700px',
      width: '1200px',
      panelClass: 'custom-dialog-container'
    });
    dialog.componentInstance.product = product;
  }
}
